import { useEffect, useRef, useState } from 'react';
import { ExternalLink, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { progressApi, type StrategyReadiness } from '../../lib/api';
import { RoutineBuilder } from './Routine';
import { ICE, TEAL, ACCENT, FIELD, PANEL, PX_FONT, TITLE_FONT, HEAD_SHADOW, pixelFrame } from '../guild/theme';

/* The Readiness Progression board, carried over from the Inevitrade Notion tracker: three phases of
   setup tasks (the Notion board's Information phase is dropped — members have done it by the time they're here). The phase you're in is worked out from your trades, not ticked by hand. */

export const COURSE_URL = 'https://inevitrade.teachable.com/courses/inevitrade-crypto-accelerator/lectures/58139215';

type PhaseKey = 'replay' | 'paper' | 'next';

/** What the trader's logged trades say (the board passes in just the strategy being viewed) */
interface Progress { anyReplay: boolean; replayDone: boolean; anyPaper: boolean; paperDone: boolean; replayDays: number; paperTrades: number; replayExp: number; paperExp: number }
const progressOf = (readiness: Record<string, StrategyReadiness> | null): Progress => {
  const all = Object.values(readiness ?? {});
  return {
    anyReplay: all.some(r => r.backtestTrades > 0),
    replayDone: all.some(r => r.paperUnlocked),
    anyPaper: all.some(r => r.paperTrades > 0),
    paperDone: all.some(r => r.ready),
    replayDays: Math.max(0, ...all.map(r => r.backtestDays)),
    paperTrades: Math.max(0, ...all.map(r => r.paperTrades)),
    replayExp: all[0]?.backtestExpectancy ?? 0,
    paperExp: all[0]?.paperExpectancy ?? 0,
  };
};

/** Expectancy must be positive too, so it rides along with the count */
const fmtExp = (r: number) => `${r >= 0 ? '+' : ''}${r.toFixed(2)}R`;

/** A task either ticks itself off from the trader's data (`auto`) or is ticked by hand (`key`) */
type Task = { text: string } & (
  | { auto: (p: Progress) => boolean; key?: never; choices?: never; gate?: never; builder?: never;
      /** Carries the strategy switcher */
      picker?: boolean;
      /** What to do next, shown on the right until the step is done */
      hint?: string;
      /** The step's target and the count so far, always shown on the right */
      note?: (p: Progress) => string }
  | { key: string; auto?: never;
      /** Answered by picking one option instead of a tick */
      choices?: [string, string][];
      /** Can't be done until the trader's data says so */
      gate?: (p: Progress) => boolean; picker?: never; hint?: never; note?: never;
      /** Done by building something in a popup (the trading routine) rather than ticking */
      builder?: boolean }
);

const PHASES: { key: PhaseKey; name: string; tone: string; tasks: Task[] }[] = [
  { key: 'replay', name: 'Replay', tone: '#4C8DFF', tasks: [
    { text: 'Choose your strategy', auto: () => true, picker: true },  // always lit: the journey starts here
    { text: 'Commence bar replay', auto: p => p.anyReplay, hint: 'Enter first trade' },
    { text: 'Finish bar replay', auto: p => p.replayDone, note: p => `${Math.min(p.replayDays, 30)}/30 trading days${p.replayDays ? ` · ${fmtExp(p.replayExp)}` : ''}` },
  ] },
  { key: 'paper', name: 'Paper', tone: '#F0A04B', tasks: [
    { text: 'Set up a sim/demo account', key: 'sim_account', gate: p => p.replayDone },
    { text: 'Commence paper trading', auto: p => p.anyPaper },
    { text: 'Finish paper trading', auto: p => p.paperDone, note: p => `${Math.min(p.paperTrades, 30)}/30 trades${p.paperTrades ? ` · ${fmtExp(p.paperExp)}` : ''}` },
  ] },
  { key: 'next', name: 'Next steps', tone: '#34F5C5', tasks: [
    { text: 'Open a trading account', key: 'open_account', choices: [['live', 'Live'], ['prop', 'Prop']], gate: p => p.paperDone },
    { text: 'Design your trading routine', key: 'trading_routine', gate: p => p.paperDone, builder: true },
    { text: 'Generate your prescription · opens the Prescription Engine', auto: p => p.paperDone },
  ] },
];

/** Furthest phase reached (for the strategy being viewed) */
function currentPhase(readiness: Record<string, StrategyReadiness> | null): PhaseKey {
  const all = Object.values(readiness ?? {});
  if (all.some(r => r.ready)) return 'next';
  if (all.some(r => r.paperUnlocked)) return 'paper';
  return 'replay';
}

const pickBtn = { display: 'grid', placeItems: 'center', width: 22, height: 22, padding: 0, border: 'none', background: 'none', color: ICE, cursor: 'pointer' } as const;

const label = { fontFamily: PX_FONT, fontSize: 10, letterSpacing: '0.12em', color: TEAL } as const;

export function ProgressionBoard({ username, readiness: all, strategy, strategies, onStrategy, onCreate, creating }: {
  username: string;
  readiness: Record<string, StrategyReadiness> | null;
  /** The board follows one strategy at a time, switched from the "Choose your strategy" step */
  strategy: string;
  strategies: { code: string; name: string }[];
  onStrategy: (code: string) => void;
  onCreate: () => void;
  creating: boolean;
}) {
  const readiness = all?.[strategy] ? { [strategy]: all[strategy] } : null;
  const at = Math.max(0, strategies.findIndex(s => s.code === strategy));
  const cycle = (step: number) => strategies.length && onStrategy(strategies[(at + step + strategies.length) % strategies.length].code);
  const [done, setDone] = useState<Set<string>>(new Set());
  const [values, setValues] = useState<Record<string, string>>({});
  const [building, setBuilding] = useState(false);
  useEffect(() => {
    progressApi.list(username).then(r => { setDone(new Set(r.done)); setValues(r.values); }).catch(() => undefined);
  }, [username]);
  /** Pick (or un-pick) a choice task's option */
  const choose = (key: string, value: string) => {
    const clear = values[key] === value;
    setValues(v => { const n = { ...v }; if (clear) delete n[key]; else n[key] = value; return n; });
    setDone(s => { const n = new Set(s); if (clear) n.delete(key); else n.add(key); return n; });
    progressApi.set(username, key, !clear, clear ? undefined : value).catch(() => undefined);
  };
  // The shared sheen needs the board's width and each column's offset (3 equal columns, 12px gaps)
  const boardRef = useRef<HTMLDivElement>(null);
  const [boardW, setBoardW] = useState(900);
  useEffect(() => {
    const el = boardRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setBoardW(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Each lit step's sheen is phase-locked to the wall clock from the moment it lights, so steps that
  // finish later join the same sweep; the delay is kept stable across re-renders
  const delays = useRef(new Map<string, number>());
  const sheenDelay = (id: string, lit: boolean) => {
    if (!lit) { delays.current.delete(id); return 0; }
    if (!delays.current.has(id)) delays.current.set(id, Date.now() % 10000);
    return delays.current.get(id)!;
  };
  const colX = (col: number) => col * ((boardW - 24) / 3 + 12);
  const here = currentPhase(readiness);
  const progress = progressOf(readiness);
  const isDone = (task: Task) => (task.auto ? task.auto(progress) : done.has(task.key!));
  const toggle = (key: string) => {
    const on = !done.has(key);
    setDone(s => { const n = new Set(s); if (on) n.add(key); else n.delete(key); return n; });
    progressApi.set(username, key, on).catch(() => setDone(s => { const n = new Set(s); if (on) n.delete(key); else n.add(key); return n; }));
  };

  return (
    <div style={{ background: PANEL, boxShadow: pixelFrame(), borderRadius: 'var(--pal-radius, 0)', padding: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className="pal-chapter" style={{ fontFamily: TITLE_FONT, fontSize: 20, color: ICE, textShadow: HEAD_SHADOW }}>Progression board</span>
        {all?.[strategy]?.fastTrack && (
          <span title="Gates are open without the normal replay → paper path" style={{ ...label, fontWeight: 800, color: 'var(--pal-on-accent)', background: ACCENT, padding: '3px 9px', borderRadius: 10 }}>
            FAST-TRACKED · {({ staff: 'Moderator', veteran: 'Verified veteran', live_record: 'Live track record' } as const)[all[strategy].fastTrack!]}
          </span>
        )}
        <a href={COURSE_URL} target="_blank" rel="noreferrer" style={{ ...label, color: ACCENT, display: 'flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}>
          How to use this · course module <ExternalLink size={12} />
        </a>
      </div>
      <div ref={boardRef} className="pal-board" style={{ ['--board-w' as string]: `${boardW}px`, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
        {PHASES.map((ph, col) => {
          const on = ph.key === here;
          const count = ph.key === 'replay' || (ph.key === 'paper' ? progress.replayDone : progress.paperDone) ? ph.tasks.filter(isDone).length : 0;
          return (
            <div key={ph.key} style={{
              padding: 10, borderRadius: 'var(--pal-radius, 0)', background: FIELD,
              boxShadow: on ? `0 0 0 1.5px ${ph.tone}, 0 0 18px ${ph.tone}55` : 'var(--pal-field-shadow)',
              display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ ...label, fontWeight: 700, color: ph.tone, padding: '2px 8px', background: `${ph.tone}22`, borderRadius: 10 }}>{ph.name}</span>
                <span style={{ ...label, fontSize: 9 }}>{count}/{ph.tasks.length}</span>
              </div>
              {/* Same slot in every column so the task rows line up across phases */}
              <span className={on ? 'pal-pulse' : undefined} style={{ ...label, fontSize: 9, color: ph.tone, height: 12, visibility: on ? 'visible' : 'hidden' }}>● You are here</span>
              {ph.tasks.map(task => {
                // A phase only lights once the one before it is complete for this strategy
                const phaseOpen = ph.key === 'replay' || (ph.key === 'paper' ? progress.replayDone : progress.paperDone);
                const d = phaseOpen && isDone(task);
                const auto = Boolean(task.auto);
                const locked = Boolean(task.gate && !task.gate(progress));
                const asGroup = Boolean(task.choices || task.picker || task.builder);
                const Card = asGroup ? 'div' : 'button';
                return (
                  <Card key={task.text} {...(asGroup ? { role: 'group', 'aria-label': task.text } : {
                      type: 'button' as const, 'aria-disabled': auto || locked, 'aria-pressed': d,
                      // Not `disabled`: disabled buttons swallow hover, so the tooltip saying why would never show
                      onClick: () => { if (!auto && !locked && task.key) toggle(task.key); } })}
                    title={auto ? 'Ticks itself off from your logged trades' : locked ? (ph.key === 'paper' ? 'Unlocks once replay is complete' : 'Unlocks once paper trading is complete') : undefined}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, minHeight: 50, padding: '8px 11px', border: 'none',
                      cursor: auto || asGroup ? 'default' : locked ? 'default' : 'pointer', textAlign: 'left', borderRadius: 'var(--pal-radius, 0)',
                      // Locked steps look like any other not-yet-done step; they just don't respond
                      // Done = the card lights up in its phase's own colour; no checkboxes
                      // Every step carries its phase's edge; a finished step takes a light wash with a shimmer running through it
                      ['--tone' as string]: ph.tone,
                      // Offset down the column (card + gap ≈ 58px) × tan 25°, so the shimmer lines up across cards
                      ['--shift' as string]: `${Math.round(ph.tasks.indexOf(task) * 58 * 0.466)}px`,
                      ['--col-x' as string]: `${Math.round(colX(col))}px`,
                      ['--sheen-delay' as string]: `-${sheenDelay(task.text, d)}ms`,
                      background: d ? `${ph.tone}1F` : PANEL,
                      boxShadow: `inset 3px 0 0 ${d ? ph.tone : `${ph.tone}66`}, inset 0 0 0 1px ${d ? `${ph.tone}55` : 'var(--pal-rim)'}`,
                      transition: 'background 0.3s, box-shadow 0.3s' }}
                    className={d ? 'pal-shimmer' : undefined}>
                    <span style={{ fontFamily: PX_FONT, fontSize: 12, fontWeight: d ? 600 : 500, color: ICE, position: 'relative' }}>{task.text}</span>
                    {task.note && (
                      <span style={{ fontFamily: PX_FONT, fontSize: 12, fontWeight: 800, color: ph.tone, whiteSpace: 'nowrap', position: 'relative' }}>{task.note(progress)}</span>
                    )}
                    {task.hint && !d && (
                      <span style={{ fontFamily: PX_FONT, fontSize: 12, fontWeight: 800, color: ph.tone, whiteSpace: 'nowrap', position: 'relative' }}>{task.hint} ↓</span>
                    )}
                    {task.picker && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative' }} onClick={e => e.stopPropagation()}>
                        <button type="button" aria-label="Previous strategy" onClick={() => cycle(-1)} style={pickBtn}><ChevronLeft size={14} /></button>
                        <span title={strategies[at]?.name} style={{ fontFamily: PX_FONT, fontSize: 12, fontWeight: 800, color: ph.tone, minWidth: 44, textAlign: 'center' }}>{creating ? 'New' : strategy}</span>
                        <button type="button" aria-label="Next strategy" onClick={() => cycle(1)} style={pickBtn}><ChevronRight size={14} /></button>
                        <button type="button" aria-label="New strategy" title="Create your own strategy" onClick={onCreate}
                          style={{ ...pickBtn, marginLeft: 4, boxShadow: `inset 0 0 0 1px ${ph.tone}88`, borderRadius: 10, color: creating ? ph.tone : ICE }}><Plus size={13} /></button>
                      </span>
                    )}
                    {task.builder && (
                      <button type="button" aria-disabled={locked} onClick={() => { if (!locked) setBuilding(true); }}
                        title={locked ? 'Unlocks once paper trading is complete' : undefined}
                        style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: PX_FONT, fontSize: 11, fontWeight: 800, padding: '4px 10px', border: 'none', borderRadius: 10,
                          position: 'relative', background: d ? 'transparent' : 'transparent', color: d ? ph.tone : ICE, boxShadow: `inset 0 0 0 1px ${ph.tone}88`, cursor: 'pointer' }}>
                        {d ? 'Edit' : <><Plus size={12} /> Build</>}
                      </button>
                    )}
                    {task.choices && (
                      <span style={{ display: 'flex', gap: 4, position: 'relative' }}>
                        {task.choices.map(([v, name]) => {
                          const picked = values[task.key!] === v;
                          return (
                            <button key={v} type="button" aria-disabled={locked} aria-pressed={picked} title={locked ? 'Unlocks once paper trading is complete' : undefined} onClick={() => { if (!locked) choose(task.key!, v); }}
                              style={{ fontFamily: PX_FONT, fontSize: 10, fontWeight: 700, padding: '4px 9px', border: 'none', borderRadius: 10,
                                cursor: locked ? 'default' : 'pointer',
                                background: picked ? ph.tone : 'transparent', color: picked ? 'var(--pal-on-accent)' : ICE,
                                boxShadow: picked ? 'none' : `inset 0 0 0 1px ${ph.tone}88` }}>
                              {name}
                            </button>
                          );
                        })}
                      </span>
                    )}
                  </Card>
                );
              })}
            </div>
          );
        })}
      </div>
      {building && (
        <RoutineBuilder username={username} onClose={() => setBuilding(false)}
          onSaved={() => setDone(s => new Set(s).add('trading_routine'))} />
      )}
    </div>
  );
}
